import React, { useState, useEffect, useRef } from 'react';
import { FaPlay, FaPause, FaVolumeUp, FaCheck, FaTimes, FaRobot, FaStar, FaHeart, FaLightbulb, FaSpinner } from 'react-icons/fa';
import { elevenLabsListener, type SpanishVoiceName } from '../../lib/listener';

interface Song {
  id: string;
  song_url: string;
  song_cover: string;
  creator: string;
  language: string;
  song_lyrics: string;
}

interface ListenWriteExerciseProps {
  song: Song;
  onComplete: (results: any) => void;
}

interface AudioSegment {
  id: number;
  text: string;
  startTime: number;
  duration: number;
  elevenLabsAudio?: string;
}

export default function ListenWriteExercise({ song, onComplete }: ListenWriteExerciseProps) {
  const [segments, setSegments] = useState<AudioSegment[]>([]);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [showResults, setShowResults] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [startTime] = useState(Date.now());
  const [useElevenLabs, setUseElevenLabs] = useState(true);
  const [generatingAudio, setGeneratingAudio] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState<SpanishVoiceName>('maria');
  const [streak, setStreak] = useState(0);
  const [audioError, setAudioError] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const elevenLabsAudioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    generateSegments();
    if (audioRef.current) {
      audioRef.current.src = song.song_url;
    }
  }, []);

  const generateSegments = () => {
    const cleanedLyrics = song.song_lyrics
      .replace(/\[.*?\]/g, '')
      .replace(/\(.*?\)/g, '')
      .replace(/\n+/g, '\n')
      .trim();

    const lines = cleanedLyrics
      .split('\n')
      .map(line => line.trim())
      .filter(line => {
        return line.length > 10 && 
               line.split(' ').length >= 3 && 
               line.split(' ').length <= 12 &&
               !line.match(/^[A-Z\s]+$/) &&
               line.match(/[a-záéíóúñü]/i);
      })
      .slice(0, 6);

    const generatedSegments: AudioSegment[] = lines.map((line, index) => ({
      id: index,
      text: line.trim(),
      startTime: index * 15,
      duration: 12
    }));

    setSegments(generatedSegments);
  };

  const generateElevenLabsAudio = async (text: string): Promise<string | null> => {
    try {
      setGeneratingAudio(true);
      setAudioError(null);
      
      if (!elevenLabsListener.isAvailable()) {
        throw new Error('ElevenLabs service is not available. Please check your API key.');
      }

      const audioUrl = await elevenLabsListener.generateSpanishSpeech(
        text,
        selectedVoice,
        0.85, // Slightly slower for learning
        0.8,  // High stability for clear pronunciation
        0.9   // High clarity for learning
      );

      if (!audioUrl) {
        throw new Error('Failed to generate audio with ElevenLabs');
      }
      
      return audioUrl;
    } catch (error: any) {
      console.error('Error generating ElevenLabs audio:', error);
      setAudioError(error.message);
      return null;
    } finally {
      setGeneratingAudio(false);
    }
  };

  const playSegment = async () => {
    if (segments.length === 0) return;

    const segment = segments[currentSegment];
    
    if (useElevenLabs) {
      if (!segment.elevenLabsAudio) {
        const audioUrl = await generateElevenLabsAudio(segment.text);
        if (audioUrl) {
          const updatedSegments = [...segments];
          updatedSegments[currentSegment].elevenLabsAudio = audioUrl;
          setSegments(updatedSegments);
          
          if (elevenLabsAudioRef.current) {
            elevenLabsAudioRef.current.src = audioUrl;
            elevenLabsAudioRef.current.play();
            setIsPlaying(true);
          }
        }
      } else {
        if (elevenLabsAudioRef.current) {
          elevenLabsAudioRef.current.src = segment.elevenLabsAudio;
          elevenLabsAudioRef.current.play();
          setIsPlaying(true);
        }
      }
    } else {
      if (!audioRef.current) return;
      
      audioRef.current.currentTime = segment.startTime;
      audioRef.current.play();
      setIsPlaying(true);

      setTimeout(() => {
        if (audioRef.current) {
          audioRef.current.pause();
          setIsPlaying(false);
        }
      }, segment.duration * 1000);
    }
  };

  const handleElevenLabsAudioEnd = () => {
    setIsPlaying(false);
  };

  const handleSubmitAnswer = () => {
    const segment = segments[currentSegment];
    const similarity = calculateSimilarity(userAnswer, segment.text);
    const isCorrect = similarity > 0.6;
    
    if (isCorrect) {
      setStreak(prev => prev + 1);
    }

    const newResult = {
      segmentId: segment.id,
      correctText: segment.text,
      userText: userAnswer,
      similarity: similarity,
      isCorrect: isCorrect
    };

    const newResults = [...results, newResult];
    setResults(newResults);

    if (currentSegment < segments.length - 1) {
      setCurrentSegment(currentSegment + 1);
      setUserAnswer('');
      setAudioError(null);
    } else {
      finishExercise(newResults);
    }
  };

  const calculateSimilarity = (text1: string, text2: string): number => {
    const normalize = (text: string) => {
      return text
        .toLowerCase()
        .replace(/[áàäâ]/g, 'a')
        .replace(/[éèëê]/g, 'e')
        .replace(/[íìïî]/g, 'i')
        .replace(/[óòöô]/g, 'o')
        .replace(/[úùüû]/g, 'u')
        .replace(/ñ/g, 'n')
        .replace(/[.,!?;:"()]/g, '')
        .trim();
    };

    const words1 = normalize(text1).split(/\s+/).filter(w => w.length > 0);
    const words2 = normalize(text2).split(/\s+/).filter(w => w.length > 0);
    
    let matches = 0;
    const maxLength = Math.max(words1.length, words2.length);
    
    words1.forEach(word => {
      if (words2.some(w2 => w2.includes(word) || word.includes(w2))) {
        matches++;
      }
    });
    
    return maxLength > 0 ? matches / maxLength : 0;
  };

  const finishExercise = (finalResults: any[]) => {
    const correctAnswers = finalResults.filter(r => r.isCorrect).length;
    const timeTaken = Math.floor((Date.now() - startTime) / 1000);
    
    setShowResults(true);
    onComplete({
      score: correctAnswers,
      totalQuestions: segments.length,
      timeTaken,
      answers: finalResults
    });
  };

  const availableVoices = elevenLabsListener.getAvailableSpanishVoices();

  const renderExercise = () => {
    const segment = segments[currentSegment];
    if (!segment) return null;

    const progress = ((currentSegment + 1) / segments.length) * 100;

    return (
      <div className="p-4 sm:p-6 lg:p-8 space-y-6 sm:space-y-8">
        {/* Animated Progress Header */}
        <div className="text-center space-y-3 sm:space-y-4">
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4 mb-3 sm:mb-4">
            <div className="flex items-center space-x-2">
              <FaStar className="text-yellow-400 animate-pulse w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-white font-bold text-base sm:text-lg">Segmento {currentSegment + 1}</span>
            </div>
            <div className="flex items-center space-x-2">
              <FaHeart className="text-red-400 w-4 h-4 sm:w-5 sm:h-5" />
              <span className="text-white font-bold text-sm sm:text-base">Racha: {streak}</span>
            </div>
          </div>
          
          {/* Animated Progress Bar */}
          <div className="relative w-full h-2 sm:h-3 bg-white/20 rounded-full overflow-hidden">
            <div 
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-green-400 to-blue-400 rounded-full transition-all duration-1000 ease-out"
              style={{ width: `${progress}%` }}
            >
              <div className="absolute inset-0 bg-white/30 animate-pulse rounded-full"></div>
            </div>
            <div className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 text-xs font-bold text-white">
              {Math.round(progress)}%
            </div>
          </div>
        </div>

        {/* Audio Mode Toggle - Mobile Responsive */}
        <div className="flex justify-center">
          <div className="bg-white/10 backdrop-blur-lg p-1 sm:p-2 rounded-xl sm:rounded-2xl flex border border-white/20 w-full max-w-md">
            <button
              onClick={() => setUseElevenLabs(false)}
              className={`flex-1 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 ${
                !useElevenLabs 
                  ? 'bg-white/20 text-white shadow-lg' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <FaVolumeUp className="inline mr-1 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Canción Original</span>
              <span className="sm:hidden">Original</span>
            </button>
            <button
              onClick={() => setUseElevenLabs(true)}
              className={`flex-1 px-3 sm:px-4 py-2 sm:py-3 rounded-lg sm:rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 ${
                useElevenLabs 
                  ? 'bg-white/20 text-white shadow-lg' 
                  : 'text-white/70 hover:text-white'
              }`}
            >
              <FaRobot className="inline mr-1 sm:mr-2 w-3 h-3 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">Voz IA Clara</span>
              <span className="sm:hidden">IA</span>
            </button>
          </div>
        </div>

        {/* Voice Selection - Only show when ElevenLabs is selected */}
        {useElevenLabs && (
          <div className="flex justify-center">
            <div className="bg-white/10 backdrop-blur-lg p-2 rounded-xl border border-white/20">
              <div className="text-center mb-2">
                <span className="text-white/80 text-xs sm:text-sm">Seleccionar Voz:</span>
              </div>
              <div className="flex flex-wrap justify-center gap-1 sm:gap-2">
                {Object.entries(availableVoices).map(([key, voice]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedVoice(key as SpanishVoiceName)}
                    className={`px-2 sm:px-3 py-1 sm:py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-300 ${
                      selectedVoice === key
                        ? 'bg-yellow-400 text-black'
                        : 'bg-white/10 text-white/80 hover:bg-white/20'
                    }`}
                  >
                    {voice.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Main Audio Card */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl text-center">
          <div className="mb-6 sm:mb-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-gradient-to-r from-green-400 to-blue-400 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6 animate-pulse">
              <FaVolumeUp className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
            </div>
            <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4">
              {useElevenLabs ? 'Escucha la IA' : 'Escucha la Canción'}
            </h3>
            <p className="text-white/80 mb-4 sm:mb-6 text-sm sm:text-base px-2">
              {useElevenLabs 
                ? 'Escucha la pronunciación clara y escribe exactamente lo que oyes'
                : 'Escucha atentamente este segmento y escribe lo que oyes en español'
              }
            </p>
            
            {/* Error Display */}
            {audioError && (
              <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-4">
                <p className="text-red-200 text-sm">{audioError}</p>
              </div>
            )}
            
            <button
              onClick={playSegment}
              disabled={isPlaying || generatingAudio}
              className="flex items-center justify-center mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 bg-gradient-to-r from-green-400 to-green-600 text-white rounded-xl sm:rounded-2xl hover:from-green-500 hover:to-green-700 disabled:opacity-50 transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
            >
              {generatingAudio ? (
                <>
                  <FaSpinner className="animate-spin mr-2 sm:mr-3 w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Generando Audio...</span>
                  <span className="sm:hidden">Generando...</span>
                </>
              ) : isPlaying ? (
                <>
                  <FaPause className="mr-2 sm:mr-3 w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">Reproduciendo...</span>
                  <span className="sm:hidden">Reproduciendo</span>
                </>
              ) : (
                <>
                  <FaPlay className="mr-2 sm:mr-3 w-4 h-4 sm:w-5 sm:h-5" />
                  <span className="hidden sm:inline">{useElevenLabs ? 'Reproducir IA' : 'Reproducir'}</span>
                  <span className="sm:hidden">Reproducir</span>
                </>
              )}
            </button>
            
            {useElevenLabs && (
              <p className="text-white/60 text-xs sm:text-sm mt-3 sm:mt-4 px-2">
                🤖 The AI will pronounce the text clearly for better comprehension
              </p>
            )}
          </div>

          {/* Answer Input */}
          <div className="space-y-3 sm:space-y-4">
            <textarea
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Escribe lo que escuchas en español..."
              className="w-full p-3 sm:p-4 lg:p-6 bg-white/20 backdrop-blur-lg border border-white/30 rounded-xl sm:rounded-2xl text-white placeholder-white/50 focus:ring-2 focus:ring-green-400 focus:border-transparent resize-none text-sm sm:text-base lg:text-lg font-medium transition-all duration-300"
              rows={3}
              style={{ minHeight: '80px' }}
            />
            
            {/* Study Tip - General guidance without revealing answers */}
            <div className="flex justify-center">
              <div className="p-3 sm:p-4 bg-blue-400/10 backdrop-blur-lg rounded-xl sm:rounded-2xl border border-blue-400/20">
                <p className="text-blue-200 text-xs sm:text-sm">
                  💡 No te preocupes por los acentos, concéntrate en las palabras principales
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="text-center">
          <button
            onClick={handleSubmitAnswer}
            disabled={!userAnswer.trim()}
            className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-400 to-purple-600 text-white rounded-xl sm:rounded-2xl hover:from-purple-500 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
          >
            <span className="hidden sm:inline">
              {currentSegment < segments.length - 1 ? 'Siguiente Segmento' : 'Terminar Ejercicio'}
            </span>
            <span className="sm:hidden">
              {currentSegment < segments.length - 1 ? 'Siguiente' : 'Terminar'}
            </span>
            <FaCheck className="ml-2 sm:ml-3 w-3 h-3 sm:w-4 sm:h-4" />
          </button>
        </div>

        {/* Hidden audio elements */}
        <audio 
          ref={audioRef} 
          onEnded={() => setIsPlaying(false)}
        />
        <audio 
          ref={elevenLabsAudioRef} 
          onEnded={handleElevenLabsAudioEnd}
        />
      </div>
    );
  };

  const renderResults = () => {
    const correctAnswers = results.filter(r => r.isCorrect).length;
    const percentage = Math.round((correctAnswers / segments.length) * 100);
    const avgSimilarity = Math.round(results.reduce((sum, r) => sum + r.similarity, 0) / results.length * 100);

    return (
      <div className="p-4 sm:p-6 lg:p-8 text-center space-y-6 sm:space-y-8">
        {/* Celebration Header */}
        <div className="space-y-4 sm:space-y-6">
          <div className="text-6xl sm:text-7xl lg:text-8xl animate-bounce">🎧</div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-4">¡Fantástico!</h2>
          <div className="text-6xl sm:text-7xl lg:text-8xl font-bold bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent animate-pulse">
            {percentage}%
          </div>
          <p className="text-white/80 text-base sm:text-lg lg:text-xl px-4">
            Obtuviste {correctAnswers} de {segments.length} segmentos correctos
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-2 sm:space-y-0 sm:space-x-4">
            <div className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-green-400/20 rounded-full">
              <FaStar className="text-green-400 w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-white font-bold text-xs sm:text-sm">Similitud: {avgSimilarity}%</span>
            </div>
            <div className="flex items-center space-x-2 px-3 sm:px-4 py-2 bg-red-400/20 rounded-full">
              <FaHeart className="text-red-400 w-3 h-3 sm:w-4 sm:h-4" />
              <span className="text-white font-bold text-xs sm:text-sm">Racha: {streak}</span>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="bg-white/10 backdrop-blur-lg rounded-2xl sm:rounded-3xl p-4 sm:p-6 border border-white/20">
          <h3 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">Revisa tus Respuestas</h3>
          <div className="space-y-3 sm:space-y-4 max-h-48 sm:max-h-60 overflow-y-auto">
            {results.map((result, index) => (
              <div key={index} className="text-left bg-white/5 rounded-lg sm:rounded-xl p-3 sm:p-4 border-b border-white/10">
                <div className="flex items-center mb-2">
                  <span className="font-medium text-white mr-2 text-sm sm:text-base">Segmento {index + 1}:</span>
                  {result.isCorrect ? (
                    <FaCheck className="text-green-400 w-3 h-3 sm:w-4 sm:h-4" />
                  ) : (
                    <FaTimes className="text-red-400 w-3 h-3 sm:w-4 sm:h-4" />
                  )}
                  <span className="ml-2 text-xs sm:text-sm text-white/60">
                    ({Math.round(result.similarity * 100)}% similitud)
                  </span>
                </div>
                <div className="space-y-1 sm:space-y-2">
                  <div>
                    <span className="text-white/70 text-xs sm:text-sm">Texto correcto:</span>
                    <p className="font-medium text-white text-xs sm:text-sm">{result.correctText}</p>
                  </div>
                  <div>
                    <span className="text-white/70 text-xs sm:text-sm">Tu respuesta:</span>
                    <p className={`font-medium text-xs sm:text-sm ${result.isCorrect ? 'text-green-400' : 'text-red-400'}`}>
                      {result.userText}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          onClick={() => window.location.reload()}
          className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-green-400 to-blue-600 text-white rounded-xl sm:rounded-2xl hover:from-green-500 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg font-bold text-sm sm:text-base"
        >
          Intentar de Nuevo
        </button>
      </div>
    );
  };

  if (segments.length === 0) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-4 border-green-400 border-t-transparent mx-auto mb-4"></div>
        <p className="text-white text-base sm:text-lg">Preparando segmentos de audio...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[500px] sm:min-h-[600px]">
      {showResults ? renderResults() : renderExercise()}
      
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .animate-fade-in {
          animation: fade-in 0.6s ease-out;
        }
      `}</style>
    </div>
  );
}